<template>
    <div class="condition-status-historical-trends-selector">
        <div class="condition-status-historical-trends-selector__selector">
            <div class="condition-status-historical-trends-selector__selector__field">
                <FormSingleSelectField
                    :model-value="measurementType"
                    :label="
                        t('generators.conditionStatus.content.historicalTrends.selector.labels.measurementType')
                    "
                    :placeholder-text="
                        t(
                            'generators.conditionStatus.content.historicalTrends.selector.placeholders.measurementType',
                        )
                    "
                    :options="measurementTypesOptions"
                    class="component-selector__field"
                    @update:model-value="onUpdateMeasurementType"
                />
            </div>
            <div class="condition-status-historical-trends-selector__selector__field">
                <FormSingleSelectField
                    :model-value="measurementPoint"
                    :label="
                        t('generators.conditionStatus.content.historicalTrends.selector.labels.measurementPoint')
                    "
                    :placeholder-text="
                        t(
                            'generators.conditionStatus.content.historicalTrends.selector.placeholders.measurementPoint',
                        )
                    "
                    :options="measurementPointsOptions"
                    :disabled="!measurementType"
                    class="component-selector__field"
                    @update:model-value="onUpdateMeasurementPoint"
                />
            </div>
            <div class="condition-status-historical-trends-selector__selector__field">
                <FormInputField
                    :model-value="startDate"
                    :label="t('generators.conditionStatus.content.historicalTrends.selector.labels.startDate')"
                    type="month"
                    @update:model-value="onUpdateStartDate"
                />
            </div>
            <div class="condition-status-historical-trends-selector__selector__field">
                <FormInputField
                    :model-value="endDate"
                    :label="t('generators.conditionStatus.content.historicalTrends.selector.labels.endDate')"
                    type="month"
                    :invalid="!isTimeframeValid"
                    :error-message="
                        t('generators.conditionStatus.content.historicalTrends.selector.errors.invalidTimeFrame')
                    "
                    @update:model-value="onUpdateEndDate"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, toRefs, watch } from 'vue'
import { compareAsc } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import FormSingleSelectField from '@/components/form-inputs/form-single-select-field/FormSingleSelectField.vue'
import FormInputField from '@/components/form-inputs/form-input-field/FormInputField.vue'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import { HistoricalTrendsSelector } from '@/pages/generators/condition-status/sections/historical-trends/historical-trends-types'

const { t } = useI18n()

const props = defineProps<{
    measurementType?: number
    measurementPoint?: number
    startDate?: string
    endDate?: string
}>()

const emit = defineEmits<{
    'update:selector': [value: HistoricalTrendsSelector]
}>()

const { measurementType, measurementPoint, startDate, endDate } = toRefs(props)

const isTimeframeValid = computed(() => {
    if (!startDate.value || !endDate.value) {
        return false
    }

    return compareAsc(new Date(startDate.value), new Date(endDate.value)) === -1
})

const { data: constants } = useConstantsQuery()

const measurementTypes = computed(() => constants.value?.generatorHistoricalTrends?.options ?? [])
const measurementTypesOptions = computed<SelectOptions<number>>(
    () =>
        measurementTypes.value.map((type) => ({
            label: type.translationKey ? t(type.translationKey) : '-',
            value: type.id ?? -1,
        })) ?? [],
)

const measurementPointsOptions = computed<SelectOptions<number>>(() => {
    return measurementTypes.value
        .find((type) => type.id === props.measurementType)
        ?.options?.map((point) => ({
            label: point.translationKey ? t(point.translationKey) : '-',
            value: point.id ?? -1,
        })) ?? []
})

const onUpdateMeasurementType = (value: number | undefined) => {
    emit('update:selector', {
        measurementType: value,
        measurementPoint: measurementPoint.value,
        startDate: startDate.value,
        endDate: endDate.value,
    })
}

const onUpdateMeasurementPoint = (value: number | undefined) => {
    emit('update:selector', {
        measurementType: measurementType.value,
        measurementPoint: value,
        startDate: startDate.value,
        endDate: endDate.value,
    })
}

const onUpdateStartDate = (value: string | undefined) => {
    emit('update:selector', {
        measurementType: measurementType.value,
        measurementPoint: measurementPoint.value,
        startDate: value,
        endDate: endDate.value,
    })
}

const onUpdateEndDate = (value: string | undefined) => {
    emit('update:selector', {
        measurementType: measurementType.value,
        measurementPoint: measurementPoint.value,
        startDate: startDate.value,
        endDate: value,
    })
}

const selectFirstMeasurementType = async () => {
    const firstMeasurementTypeId = measurementTypes.value?.[0]?.id

    if (!firstMeasurementTypeId) {
        return
    }

    await nextTick()
    onUpdateMeasurementType(firstMeasurementTypeId)
}

const selectFirstMeasurementPoint = async () => {
    const selectedMeasurementType = measurementTypes.value.find((type) => type.id === measurementType.value)
    const firstMeasurementPoint = selectedMeasurementType?.options?.[0]?.id

    if (!firstMeasurementPoint) {
        return
    }

    await nextTick()
    onUpdateMeasurementPoint(firstMeasurementPoint)
}

watch(() => measurementTypes.value, async () => {
    selectFirstMeasurementType()
}, { immediate: true })

watch(() => measurementType.value, async () => {
    await selectFirstMeasurementPoint()
})
</script>

<style scoped lang="scss">
.condition-status-historical-trends-selector {
    &__selector {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 1em;

        &__field {
            min-width: 300px;
        }
    }
}
</style>
